import { getLanguage, reactive, saveLanguage } from 'cdui-js';

/** 类型仅取自简体 JSON，不打进运行时包 */
import type zh_CN from './languages/zh-CN.json';
import { commonState } from '../stores/common';
import { refreshBusConfigsByLang } from '../stores/bus-config';

const supports = ['zh-CN', 'zh-TW', 'en'] as const;
export type H5Lang = (typeof supports)[number];

export const serviceLang = {
  'zh-CN': 'CH',
  'zh-TW': 'HK',
  en: 'en',
};

export type I18nType = typeof zh_CN;

type LangModule = { default: I18nType };

/** 语言包 URL（构建期落成 json 资产，不进 JS chunk 图，避免 minChunkSize 误吸运行时） */
const LANG_URLS: Record<H5Lang, string> = {
  'zh-CN': new URL('./languages/zh-CN.json', import.meta.url).href,
  'zh-TW': new URL('./languages/zh-TW.json', import.meta.url).href,
  en: new URL('./languages/en.json', import.meta.url).href,
};

const loadLangAsset = async (url: string): Promise<LangModule> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to load i18n pack: ${res.status}`);
  return { default: (await res.json()) as I18nType };
};

/** 启动 / 切换时只 fetch 当前语言 */
const loaders: Record<H5Lang, () => Promise<LangModule>> = {
  'zh-CN': () => loadLangAsset(LANG_URLS['zh-CN']),
  'zh-TW': () => loadLangAsset(LANG_URLS['zh-TW']),
  en: () => loadLangAsset(LANG_URLS.en),
};

const packCache: Partial<Record<H5Lang, I18nType>> = {};

const pendingLoads: Partial<Record<H5Lang, Promise<I18nType>>> = {};

/** 解析 switchLanguage 第二参：兼容 JSON module `{ default }` 或纯对象 */
const unwrapPack = (data: I18nType | LangModule): I18nType => {
  if (data && typeof data === 'object' && 'default' in data && (data as LangModule).default) {
    return (data as LangModule).default;
  }
  return data as I18nType;
};

export async function ensurePack(lang: H5Lang): Promise<I18nType> {
  const cached = packCache[lang];
  if (cached) return cached;

  const existing = pendingLoads[lang];
  if (existing) return existing;

  const task = loaders[lang]().then((mod) => {
    const pack = unwrapPack(mod);
    packCache[lang] = pack;
    delete pendingLoads[lang];
    return pack;
  });
  pendingLoads[lang] = task;
  return task;
}

/** 规范化语言码：兼容 App `en-US`、浏览器 `zh` / `zh-HK` 等 */
export const normalizeH5Lang = (raw?: string | null): H5Lang | null => {
  if (!raw) return null;
  const lang = raw.trim();
  if (!lang) return null;
  if (lang === 'en' || lang === 'en-US' || lang.startsWith('en-')) return 'en';
  if (lang === 'zh-CN' || lang === 'zh-Hans' || lang === 'zh') return 'zh-CN';
  if (lang === 'zh-TW' || lang === 'zh-HK' || lang === 'zh-Hant' || lang.includes('zh-')) return 'zh-TW';
  if ((supports as readonly string[]).includes(lang)) return lang as H5Lang;
  return null;
};

/** URL `?lang=` 优先，保证 WebView 首屏即可同步切语言（无需等 bridge） */
const readLangFromUrl = (): H5Lang | null => {
  try {
    if (typeof location === 'undefined') return null;
    return normalizeH5Lang(new URLSearchParams(location.search).get('lang'));
  } catch {
    return null;
  }
};

export const getLang = (): H5Lang => {
  const fromUrl = readLangFromUrl();
  if (fromUrl) return fromUrl;

  let lang = getLanguage();
  const normalized = normalizeH5Lang(lang);
  return normalized ?? 'zh-CN';
};

// 目标语言（URL > 本地存储 > 默认）；文案包由 bootstrapI18n 异步到位
export let langauge: H5Lang = getLang();

/** 先占位空壳，bootstrap 后再写入；named export 指向同一 reactive 子树 */
const current = reactive({
  Header: {},
  Menu: {},
  SelfSelected: {},
  Kline: {},
  Common: {},
  AiChat: {},
} as I18nType);

const applyPackToCurrent = (pack: I18nType) => {
  const next = JSON.parse(JSON.stringify(pack)) as I18nType;
  for (const key of Object.keys(next) as (keyof I18nType)[]) {
    const src = next[key] as unknown;
    const dst = current[key] as unknown;
    if (src && typeof src === 'object' && !Array.isArray(src)) {
      if (dst && typeof dst === 'object' && !Array.isArray(dst)) {
        // 原地写入，保证 Header/Menu 等 named export 引用仍有效
        const dstObj = dst as Record<string, unknown>;
        for (const k of Object.keys(dstObj)) delete dstObj[k];
        Object.assign(dstObj, src);
      } else {
        (current as Record<string, unknown>)[key as string] = src;
      }
    } else {
      (current as Record<string, unknown>)[key as string] = src;
    }
  }
};

const syncLangSideEffects = (next: H5Lang) => {
  try {
    refreshBusConfigsByLang(next);
  } catch {
    // ignore
  }
  try {
    commonState.langauge = next;
  } catch {
    // commonState 可能尚未初始化完成时忽略
  }
};

let bootPromise: Promise<I18nType> | null = null;

/**
 * 启动只拉当前语言一份；须在 render App 前 await。
 * SSR 注入的全局 I18N 可跳过网络。
 */
export function bootstrapI18n(): Promise<I18nType> {
  if (bootPromise) return bootPromise;

  bootPromise = (async () => {
    // @ts-ignore
    if (typeof I18N !== 'undefined') {
      // @ts-ignore
      const pack = I18N as I18nType;
      packCache[langauge] = pack;
      applyPackToCurrent(pack);
    } else {
      const pack = await ensurePack(langauge);
      applyPackToCurrent(pack);
    }

    if (readLangFromUrl()) {
      saveLanguage(langauge);
    }
    syncLangSideEffects(langauge);
    return packCache[langauge]!;
  })();

  return bootPromise;
}

/**
 * 页头
 */
export const Header = current.Header;

/**
 * 菜单
 */
export const Menu = current.Menu;
/**
 * 自选
 */
export const SelfSelected = current.SelfSelected;

/**
 * K线图
 */
export const Kline = current.Kline;
/**
 * 通用
 */
export const Common = current.Common;

/**
 * 智能 AI 对话
 */
export const AiChat = current.AiChat;

/**
 * 切换语言（按需动态加载 JSON）
 *
 * @param name 语言名称
 * @param data 可选；已加载的包或 `import('*.json')` 的 module
 */
export const switchLanguage = async (
  name: string,
  data?: I18nType | LangModule,
): Promise<H5Lang> => {
  const next = normalizeH5Lang(name) ?? 'zh-CN';
  const pack = data ? unwrapPack(data) : await ensurePack(next);
  packCache[next] = pack;
  saveLanguage(next);
  langauge = next;
  applyPackToCurrent(pack);
  syncLangSideEffects(next);
  return next;
};

/** 供 RN bridge / URL 调用：按语言码切换并同步文案 */
export const applyH5Lang = async (raw?: string | null): Promise<H5Lang> => {
  const next = normalizeH5Lang(raw);
  if (!next) return langauge;
  // 目标语言文案尚未加载时仍要 ensure（启动阶段 langauge 已是目标码）
  if (next === langauge && packCache[next]) {
    return langauge;
  }
  return switchLanguage(next);
};

export default current;
